import { Component, NgModule, Input, Output, EventEmitter, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, IUser } from '../../services';
import { UserPanelModule } from '../user-panel/user-panel.component';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxToolbarModule } from 'devextreme-angular/ui/toolbar';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MessagesService } from '../../services/messages.service';
import { GetUnreadMessageCountsRequest, GetUnreadMessageCountsResponse } from '../../models/messages.models';
import { MessageNotificationService } from '../../services/message-notification.service';
import { ActiveUserLocalDataService } from '../../services/active-user-local-data.service';
import { CourierService } from '../../services/courier.service';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  user: any = null;

  coordinatePosition = { top: 2, left: 230 };
  unreadMessageCount: number = 0;
  isMobile: boolean = false;

  @Output()
  menuToggle = new EventEmitter<boolean>();

  @Output()
  languageChanged = new EventEmitter<string>();

  @Input()
  menuToggleEnabled = false;

  @Input()
  title!: string;

userName: string = '';
userSurname: string = '';

  languages = [
    { code: 'en', name: 'EN' },
    { code: 'lv', name: 'LV' },
    { code: 'ee', name: 'EE' },
    { code: 'lt', name: 'LT' }
  ];

  fontSizes = [
    { size: 'small', label: 'Small' },
    { size: 'medium', label: 'Medium' },
    { size: 'large', label: 'Large' }
  ];

  selectedLanguage: string = 'lv';

  userMenuItems: any[] = [];

  constructor(
    private authService: AuthService, 
    private router: Router,
    private translate: TranslateService,
    private messagesService: MessagesService,
    private messageNotificationService: MessageNotificationService,
    private activeUserLocalDataService: ActiveUserLocalDataService,
    private courierService: CourierService,
    private usersService: UsersService
  ) { 
  }

  ngOnInit() {
    this.checkIfMobile();
    // Fetch user info for header display
    this.usersService.getUserInfo().subscribe((response: any) => {
      this.userName = response.name;
      this.userSurname = response.surname;
    });
    this.userMenuItems = [
      {
        text: 'Profile',
        icon: 'user',
        onClick: () => {
          this.router.navigate(['/profile']);
        }
      },
      {
        text: 'Logout',
        icon: 'runner',
        onClick: () => {
          this.authService.logOut();
        },
      },
      {
        text: 'Refresh',
        icon: 'refresh',
        onClick: () => {
          this.refreshPage();
        }
      },
      {
        text: 'Home',
        icon: 'home',
        onClick: () => {
          this.navigateToHome();
        }
      },
      {
        text: 'Messages',
        icon: 'message',
        onClick: () => {
          this.navigateToMessages();
        }
      },
      {
        text: 'Contacts',
        icon: 'tel',
        onClick: () => {
          this.navigateToContacts();
        }
      },
      {
        text: 'Languages',
        icon: 'globe',
        items: []
      },
      {
        text: 'EN',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('en');
        }
      },
      {
        text: 'LV',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('lv');
        }
      },
      {
        text: 'EE',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('ee');
        }
      },
      {
        text: 'LT',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('lt');
        }
      },
      {
        text: 'FontSize',
        icon: 'fontsize',
        items: []
      },
      {
        text: 'Small',
        onClick: () => {
          this.changeFontSize('small');
        }
      },
      {
        text: 'Medium',
        onClick: () => {
          this.changeFontSize('medium');
        }
      },
      {
        text: 'Large',
        onClick: () => {
          this.changeFontSize('large');
        }
      }
    ];
    const storedLanguage = localStorage.getItem('lang');
    if (storedLanguage && this.languages.some(lang => lang.code === storedLanguage)) {
      this.selectedLanguage = storedLanguage;
    } else {
      const userLang = navigator.language.split('-')[0];
      this.selectedLanguage = this.languages.some(lang => lang.code === userLang) ? userLang : 'lv';
    }

    this.translate.setDefaultLang(this.selectedLanguage);
    this.translate.use(this.selectedLanguage).subscribe(
      () => {
        this.translateMenuItems();
      },
      error => {
        console.error('Error switching language:', error);  
      }
    );

    const storedFontSize = localStorage.getItem('fontSize');
    if (storedFontSize) {
      this.changeFontSize(storedFontSize);
    }

    this.checkUnreadMessages();
    setInterval(() => this.checkUnreadMessages(), 10000);
    this.messageNotificationService.unreadMessagesUpdated$.subscribe(() => {
      this.checkUnreadMessages();
    });
  }

  changeLanguage(language: string): void {
    if (this.languages.some(lang => lang.code === language)) {
      this.translate.use(language).subscribe(() => {
        this.selectedLanguage = language; 
        localStorage.setItem('lang', language); 
        this.languageChanged.emit(language);
        this.translateMenuItems();
      });
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 500;
  }

  changeFontSize(size: string): void {
    document.documentElement.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');
    document.documentElement.classList.add(`font-size-${size}`);
    localStorage.setItem('fontSize', size);
  }

  toggleMenu = () => {
    this.menuToggle.emit();
  }

  navigateToHome() {
    this.router.navigate(['/']);
  }
  navigateToMessages() {
    this.router.navigate(['/messages']);
  }
  navigateToContacts() {
    this.router.navigate(['/contacts']);
  }

  refreshPage() {
    window.location.reload();
  }

  private checkUnreadMessages() {
  // Refresh the active voucher before checking unread messages
  const refreshActiveVoucher = async () => {
    // You may want to move this logic to a shared service if needed
    const request = {};
    try {
      const response = await this.courierService.getActiveVouchers(request).toPromise();
      if (response && response.voucher) {
        this.activeUserLocalDataService.setActiveCourierVoucher(response.voucher);
      } else {
        this.activeUserLocalDataService.setActiveCourierVoucher(null);
      }
    } catch (error) {
      console.error('[Header] Error in refreshActiveVoucher:', error);
    }
  };

  refreshActiveVoucher().then(() => {
    const voucher = this.activeUserLocalDataService.getActiveCourierVoucher();
    const carId = voucher?.carsId ?? 0;
    this.usersService.getUserInfo().subscribe(user => {
      const userId = user.id;
      const request: any = { carId, userId };
      this.messagesService.getUnreadMessagesCount(request).subscribe((response: GetUnreadMessageCountsResponse) => {
        this.unreadMessageCount = response.unreadMessageCount && response.unreadMessageCount > 0 ? response.unreadMessageCount : 0;
      }, error => {
        console.error('[Header] Error fetching unread message count:', error);
      });
    });
  });
}
  
  private translateMenuItems() {
    this.userMenuItems = [
      {
        text: this.translate.instant('Profile'),
        icon: 'user',
        onClick: () => {
          this.router.navigate(['/profile']);
        }
      },
      {
        text: this.translate.instant('LogOut'),
        icon: 'runner',
        onClick: () => {
          this.authService.logOut();
        },
      },
      {
        text: this.translate.instant('Refresh'),
        icon: 'refresh',
        onClick: () => {
          this.refreshPage();
        }
      },
      {
        text: this.translate.instant('Home'),
        icon: 'home',
        onClick: () => {
          this.navigateToHome();
        }
      },
      {
        text: this.translate.instant('Contacts'),
        icon: 'tel',
        onClick: () => {
          this.navigateToContacts();
        }
      },
      {
        text: this.translate.instant('Messages'),
        icon: 'message',
        onClick: () => {
          this.navigateToMessages();
        }
      },
      {
        text: this.translate.instant('Languages'),
        icon: 'globe',
        items: []
      },
      {
        text: 'EN',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('en');
        }
      },
      {
        text: 'LV',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('lv');
        }
      },
      {
        text: 'EE',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('ee');
        }
      },
      {
        text: 'LT',
        icon: 'nothing',
        onClick: () => {
          this.changeLanguage('lt');
        }
      },
      {
        text: this.translate.instant('FontSize'),
        icon: 'fontsize',
        items: []
      },
      {
        text: this.translate.instant('Small'),
        icon: 'nothing',
        onClick: () => {
          this.changeFontSize('small');
        }
      },
      {
        text: this.translate.instant('Medium'),
        icon: 'nothing',
        onClick: () => {
          this.changeFontSize('medium');
        }
      },
      {
        text: this.translate.instant('Large'),
        icon: 'nothing',
        onClick: () => {
          this.changeFontSize('large');
        }
      }
    ];
  }

  get displayUserName(): string {
    const name = this.userName ? this.userName.trim() : '';
    const surname = this.userSurname ? this.userSurname.trim() : '';
    if (name && surname) {
      return `${name} ${surname}`;
    } else if (name) {
      return name;
    } else if (surname) {
      return surname;
    } else {
      return '';
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    DxButtonModule,
    UserPanelModule,
    DxToolbarModule,
    DxSelectBoxModule,
    DxTextBoxModule,
  ],
  declarations: [ HeaderComponent ],
  exports: [ HeaderComponent ]
})
export class HeaderModule { }